/**
 * Provider-agnostic token interface.
 * This is the canonical format for storing transcription tokens.
 * Can map from Soniox or other providers to this format.
 *
 * Shared between Convex backend and frontend.
 */
export interface TranscriptionToken {
  /** The text of the token */
  text: string;

  /** Start time in milliseconds */
  startMs: number;

  /** End time in milliseconds */
  endMs: number;

  /** Confidence score (0-1) from the transcription provider */
  confidence: number;

  /** Whether this token is finalized (vs. intermediate/non-final) */
  isFinal: boolean;

  /** Optional speaker identifier (e.g., "Speaker 1", "Dr. Smith") */
  speaker?: string;

  /** Optional language code (e.g., "en", "sl") */
  language?: string;
}

/** A single section of a generated report. */
export interface ReportSection {
  title: string;
  content: string;
}

/**
 * Default report title values by locale.
 * Used by the frontend to detect whether a title is still the untouched default.
 */
export const DEFAULT_REPORT_TITLES: Record<string, string> = {
  sl: 'Neimenovani Pacient',
  hr: 'Neimenovani Pacijent',
  en: 'Unnamed Patient',
};

const DEFAULT_REPORT_TITLES_SET = new Set(Object.values(DEFAULT_REPORT_TITLES));

/** Returns true when the title is still one of the untouched defaults. */
export function isDefaultReportTitle(title: string): boolean {
  return DEFAULT_REPORT_TITLES_SET.has(title);
}
